/**
 * Preset catalog of well-known shopping apps and websites.
 *
 * Used by the No Spend Weekend view so users can opt in with one tap. The
 * list is intentionally pragmatic — iOS uses the system FamilyActivityPicker
 * for actual selection (a token, not bundle IDs), so the `ios` hints here are
 * advisory only (to prefill the description shown before the picker). Android
 * blocks by package name directly.
 */

export interface ShoppingAppPreset {
  id: string;
  label: string;
  /** Android package name. */
  android: string;
  /** Web domain(s) also blocked when this preset is selected. */
  domains: string[];
  /** Broad category used for grouping in the UI. */
  category: 'marketplace' | 'fashion' | 'food' | 'resale' | 'travel' | 'media';
}

export const SHOPPING_APP_PRESETS: ShoppingAppPreset[] = [
  // Marketplaces
  { id: 'amazon',      label: 'Amazon',       android: 'com.amazon.mShop.android.shopping', domains: ['amazon.com', 'smile.amazon.com'], category: 'marketplace' },
  { id: 'target',      label: 'Target',       android: 'com.target.ui',                     domains: ['target.com'],                      category: 'marketplace' },
  { id: 'walmart',     label: 'Walmart',      android: 'com.walmart.android',               domains: ['walmart.com'],                     category: 'marketplace' },
  { id: 'ebay',        label: 'eBay',         android: 'com.ebay.mobile',                   domains: ['ebay.com'],                        category: 'marketplace' },
  { id: 'temu',        label: 'Temu',         android: 'com.einnovation.temu',              domains: ['temu.com'],                        category: 'marketplace' },
  { id: 'shein',       label: 'SHEIN',        android: 'com.zzkko',                         domains: ['shein.com'],                       category: 'marketplace' },
  { id: 'aliexpress',  label: 'AliExpress',   android: 'com.alibaba.aliexpresshd',          domains: ['aliexpress.com'],                  category: 'marketplace' },
  { id: 'etsy',        label: 'Etsy',         android: 'com.etsy.android',                  domains: ['etsy.com'],                        category: 'marketplace' },
  { id: 'wayfair',     label: 'Wayfair',      android: 'com.wayfair.wayfair',               domains: ['wayfair.com'],                     category: 'marketplace' },
  { id: 'costco',      label: 'Costco',       android: 'com.costco.app.android',            domains: ['costco.com'],                      category: 'marketplace' },

  // Fashion
  { id: 'nike',        label: 'Nike',         android: 'com.nike.omega',                    domains: ['nike.com'],                        category: 'fashion' },
  { id: 'zara',        label: 'Zara',         android: 'com.inditex.zara',                  domains: ['zara.com'],                        category: 'fashion' },
  { id: 'hm',          label: 'H&M',          android: 'com.hm.goe',                        domains: ['hm.com', 'www2.hm.com'],           category: 'fashion' },
  { id: 'uniqlo',      label: 'Uniqlo',       android: 'com.uniqlo.ja.catalogue',           domains: ['uniqlo.com'],                      category: 'fashion' },
  { id: 'asos',        label: 'ASOS',         android: 'com.asos.app',                      domains: ['asos.com'],                        category: 'fashion' },
  { id: 'lululemon',   label: 'Lululemon',    android: 'com.lululemon.mobile.app',          domains: ['lululemon.com', 'shop.lululemon.com'], category: 'fashion' },

  // Resale / secondhand
  { id: 'depop',       label: 'Depop',        android: 'com.depop',                         domains: ['depop.com'],                       category: 'resale' },
  { id: 'poshmark',    label: 'Poshmark',     android: 'com.poshmark.app',                  domains: ['poshmark.com'],                    category: 'resale' },
  { id: 'mercari',     label: 'Mercari',      android: 'com.mercari.ms.android',            domains: ['mercari.com'],                     category: 'resale' },
  { id: 'therealreal', label: 'The RealReal', android: 'com.therealreal.app',               domains: ['therealreal.com'],                 category: 'resale' },
  { id: 'vinted',      label: 'Vinted',       android: 'fr.vinted',            domains: ['vinted.com'],                      category: 'resale' },

  // Food delivery (impulse $ category)
  { id: 'doordash',    label: 'DoorDash',     android: 'com.dd.doordash',                   domains: ['doordash.com'],                    category: 'food' },
  { id: 'ubereats',    label: 'Uber Eats',    android: 'com.ubercab.eats',                  domains: ['ubereats.com'],                    category: 'food' },
  { id: 'grubhub',     label: 'Grubhub',      android: 'com.grubhub.android',               domains: ['grubhub.com'],                     category: 'food' },
  { id: 'instacart',   label: 'Instacart',    android: 'com.instacart.client',              domains: ['instacart.com'],                   category: 'food' },

  // Travel / "treat yourself"
  { id: 'airbnb',      label: 'Airbnb',       android: 'com.airbnb.android',                domains: ['airbnb.com'],                      category: 'travel' },
  { id: 'booking',     label: 'Booking.com',  android: 'com.booking',                       domains: ['booking.com'],                     category: 'travel' },
  { id: 'expedia',     label: 'Expedia',      android: 'com.expedia.bookings',              domains: ['expedia.com'],                     category: 'travel' },

  // Media / content-fueled impulse buys
  { id: 'tiktok-shop', label: 'TikTok Shop',  android: 'com.zhiliaoapp.musically',          domains: ['shop.tiktok.com'],                 category: 'media' },
  { id: 'pinterest',   label: 'Pinterest',    android: 'com.pinterest',                     domains: ['pinterest.com'],                   category: 'media' },
];

export const CATEGORY_LABELS: Record<ShoppingAppPreset['category'], string> = {
  marketplace: 'Marketplaces',
  fashion:     'Clothing & shoes',
  resale:      'Resale & secondhand',
  food:        'Food delivery',
  travel:      'Travel',
  media:       'Shopping-heavy social',
};

/** Ordered list of category ids for consistent UI rendering. */
export const CATEGORY_ORDER: ShoppingAppPreset['category'][] = [
  'marketplace',
  'fashion',
  'food',
  'resale',
  'travel',
  'media',
];

export function getPresetById(id: string): ShoppingAppPreset | undefined {
  return SHOPPING_APP_PRESETS.find(p => p.id === id);
}

export function collectAndroidPackageNames(selectedIds: string[]): string[] {
  return selectedIds
    .map(getPresetById)
    .filter((p): p is ShoppingAppPreset => !!p)
    .map(p => p.android);
}

export function collectDomains(selectedIds: string[], customDomains: string[] = []): string[] {
  const fromPresets = selectedIds
    .map(getPresetById)
    .filter((p): p is ShoppingAppPreset => !!p)
    .flatMap(p => p.domains);
  const merged = new Set<string>([...fromPresets, ...customDomains]);
  return [...merged];
}
